import React, { MutableRefObject, useEffect, useRef } from "react";

import { isEmpty } from "lodash/fp";
import { DateTime } from "luxon";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";

import Stack from "@mui/material/Stack";

import UpsellOverlay from "@app/analytics/MeetingReport/components/UpsellOverlay";
import useReportActions from "@app/analytics/MeetingReport/hooks/useReportActions";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import useTranscriptAutoFollow from "@app/analytics/MeetingReport/hooks/useTranscriptAutoFollow";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import type { MeetingReportSectionContext } from "@app/analytics/MeetingReport/types";
import {
  generateTranscriptItems,
  isTurnParagraph,
} from "@app/analytics/MeetingReport/utils";
import { shouldSmoothScroll } from "@app/analytics/utils";
import { elementIsVisible } from "@app/common/hooks/useVisibility";

import EmptyTranscript from "./EmptyTranscript";
import { TranscriptContainer } from "./styles";
import TranscriptEditSpeaker from "./TranscriptEditSpeaker";
import TranscriptItem from "./TranscriptItem";

interface TranscriptProps {
  context: MeetingReportSectionContext;
  scrollContainer: MutableRefObject<HTMLDivElement | null>;
}

interface TranscriptGateProps extends TranscriptProps {
  isRestrictedReport?: boolean;
}

const getVirtuosoId = (context: { section: "main" | "sidebar" }) =>
  `transcript-virtuoso-${context.section}`;

const Transcript = ({ context, scrollContainer }: TranscriptProps) => {
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const activeItemTimeout = useRef<number | null>(null);

  const includeChapters = context.section !== "sidebar";

  useTranscriptAutoFollow({ context, scrollContainer });

  const {
    summary: { chapters },
  } = useTranscript();

  const { makeJumpToTimestamp } = useReportActions();

  // TODO (ENG-10852): does it make sense to break out any of this into a separate, "video aware" Transcript?
  const { autoPlay, paused, videoStartTime } = useVideoStore((state) => ({
    autoPlay: state.autoPlay,
    paused: state.paused,
    videoStartTime: state.videoStartTime,
  }));

  const {
    activeItemIndex,
    activeItem,
    allSpeakers,
    filteredSpeakers,
    setActiveItem,
    fontSize,
    transcriptItems,
  } = useTranscriptStore((state) => ({
    activeItemIndex: state.activeItemIndex,
    activeItem: state.activeItem,
    allSpeakers: state.allSpeakers,
    filteredSpeakers: state.filteredSpeakers,
    setActiveItem: state.setActiveItem,
    fontSize: state.textSize,
    transcriptItems:
      context.section === "sidebar"
        ? state.transcriptItems
        : state.filteredTranscriptItems,
  }));

  const transcriptItemsToShow = useEditStore((state) =>
    state.editingSection === "transcript"
      ? generateTranscriptItems(chapters, state.transcript.turns)
      : transcriptItems
  );

  const { searchMode, activeSearchResult } = useSearchStore((state) => ({
    activeSearchResult: state.searchResults[state.activeSearchResultIndex],
    searchMode: state.searchMode,
  }));

  // handle scrolling to the current active turn for video playback auto-follow
  useEffect(() => {
    if (activeItem === null || activeItemIndex === null) return;

    const isParagraph = activeItem.type === "paragraph";
    if (isParagraph) {
      const turnElementRef = document.getElementById(activeItem.id);
      const currentTurnIsVisible = elementIsVisible(
        turnElementRef,
        scrollContainer.current,
        { topOffset: 100, bottomOffset: 50 }
      );
      if (currentTurnIsVisible) return;
    }

    virtuosoRef?.current?.scrollToIndex({
      index: activeItemIndex,
      behavior:
        isParagraph &&
        shouldSmoothScroll(getVirtuosoId(context), activeItemIndex)
          ? "smooth"
          : "auto",
      offset: searchMode === "replace" ? -180 : -100,
    });
  }, [activeItemIndex, activeItem, searchMode]);

  // If autoplay is false and the video isn't playing, we'll set
  // a timeout to set the active item to null. This has the effect
  // of scrolling and highlighting a turn for 2 seconds, at which
  // point the highlight disappears
  useEffect(() => {
    if (autoPlay || !paused) {
      if (activeItemTimeout.current) {
        clearTimeout(activeItemTimeout.current);
      }
      return;
    }

    if (activeItem) {
      activeItemTimeout.current = window.setTimeout(() => {
        setActiveItem(null);
      }, 2000);
    }

    return () => {
      if (activeItemTimeout.current) {
        clearTimeout(activeItemTimeout.current);
      }
    };
  }, [Boolean(activeItem), autoPlay]);

  useEffect(() => {
    if (!activeSearchResult) return;

    if (activeSearchResult?.type !== "transcript_search_result") {
      return;
    }
    const paragraphId = activeSearchResult.paragraphs[0];

    // have to set the active item when the search result changes so that
    // it will be scrolled to, but don't focus it
    setActiveItem({ id: paragraphId, type: "paragraph" }, false);
  }, [activeSearchResult?.index]);

  const jumpToTimestamp = (startTime: DateTime) => {
    if (!videoStartTime) return;
    const timestampInSeconds = startTime.diff(videoStartTime).toMillis() / 1000;
    return makeJumpToTimestamp(timestampInSeconds);
  };

  if (isEmpty(transcriptItemsToShow)) {
    const filtersApplied = filteredSpeakers.length !== allSpeakers.length;
    return (
      <Stack paddingTop={1}>
        <EmptyTranscript type={filtersApplied ? "filter" : "default"} />
      </Stack>
    );
  }

  return (
    <>
      <TranscriptEditSpeaker />
      <TranscriptContainer>
        <Stack fontSize={fontSize}>
          <Virtuoso
            id={getVirtuosoId(context)}
            ref={virtuosoRef}
            customScrollParent={scrollContainer.current!}
            data={transcriptItemsToShow}
            initialItemCount={Math.min(transcriptItemsToShow.length, 6)}
            itemContent={(index, item) => (
              <TranscriptItem
                item={item}
                context={context}
                includeChapters={includeChapters}
                onTurnClick={
                  isTurnParagraph(item)
                    ? jumpToTimestamp(item.rawStartTime)
                    : undefined
                }
                previousItem={transcriptItemsToShow[index - 1]}
                nextItem={transcriptItemsToShow[index + 1]}
              />
            )}
          />
        </Stack>
      </TranscriptContainer>
    </>
  );
};

const TranscriptGate = ({
  isRestrictedReport = false,
  ...transcriptProps
}: TranscriptGateProps) => {
  const { isLoading: transcriptIsLoading, turns } = useTranscript();
  const fontSize = useTranscriptStore((state) => state.textSize);

  if (isRestrictedReport) {
    const transcriptItems = generateTranscriptItems([], turns);
    return (
      <TranscriptContainer>
        <Stack position="relative" fontSize={fontSize}>
          <UpsellOverlay>
            {transcriptItems.slice(0, 10).map((item) => (
              <TranscriptItem
                context={transcriptProps.context}
                key={item.id}
                item={item}
              />
            ))}
          </UpsellOverlay>
        </Stack>
      </TranscriptContainer>
    );
  }

  if (!transcriptIsLoading && isEmpty(turns)) {
    return <EmptyTranscript type="default" />;
  }

  return <Transcript {...transcriptProps} />;
};

export default TranscriptGate;
